/**
 * 작성자 : 홍선영
 * 날짜 : 2026.06.18
 * 기능 : 센서 종합현황 위젯(dCd 152) 스타일
 *  - 표면/텍스트는 앱 테마 변수(--board, --text-primary 등)를 사용해 라이트/다크 모두 대응
 *  - 위험 단계 색상은 위젯 루트에 스코프된 CSS 변수로 정의(HTML 시안 팔레트 기준)
 */
import styled from 'styled-components';

export const SensorStatusRoot = styled.div`
  /* MGasInfo 와 동일한 상태색 컨벤션: 좋음=blue, 주의=green, 경고=amber, 위험=red */
  --sensor-good: var(--widget-status-blue);
  --sensor-caution: var(--widget-status-green);
  --sensor-warning: var(--widget-status-yellow);
  --sensor-danger: var(--widget-status-red);
  --sensor-none: var(--text-tertiary);
  --sensor-good-bg: var(--widget-status-tonal-blue);
  --sensor-caution-bg: var(--widget-status-tonal-green);
  --sensor-warning-bg: var(--widget-status-tonal-yellow);
  --sensor-danger-bg: var(--widget-status-tonal-red);

  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;

  /* 헤더: 타이틀(좌) / 뱃지+새로고침(우) — 다른 위젯과 동일한 레이아웃 */
  .widget-header {
    flex: 0 0 auto;
    width: 100%;
    /* 다른 대시보드 위젯과 동일한 헤더 패딩(Dashboard 컨테이너 표준값) */
    padding: 0.75rem 1rem;
    .widget-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      min-height: 2rem;
      > span {
        flex-shrink: 0;
        /* 다른 대시보드 위젯 타이틀과 동일하게 통일 (Dashboard 컨테이너 표준값) */
        font-weight: var(--font-semibold);
        font-size: 1.125rem;
        line-height: 1.2;
        color: var(--text-primary);
      }
    }
    .title-right {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    /* 새로고침 버튼: 다른 위젯(MGasInfo)과 동일한 모양 */
    .widget-btn-group {
      display: flex;
      align-items: center;
      gap: 0.125rem;
    }
    .refresh-btn {
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      border-radius: 0.5rem;
      color: var(--text-secondary);
      .material-symbols-rounded {
        font-size: 1.375rem;
      }
      &:hover {
        background-color: var(--tonal);
      }
    }
  }

  /* 상태값 뱃지 : MGasInfo 의 textBox 와 동일한 모양(색 텍스트 + tonal 배경) */
  .legend {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
    .chip {
      font-size: 0.8rem;
      font-weight: 600;
      border-radius: 5px;
      padding: 0.05rem 0.4rem;
      &.good {
        color: var(--sensor-good);
        background-color: var(--sensor-good-bg);
      }
      &.caution {
        color: var(--sensor-caution);
        background-color: var(--sensor-caution-bg);
      }
      &.warning {
        color: var(--sensor-warning);
        background-color: var(--sensor-warning-bg);
      }
      &.danger {
        color: var(--sensor-danger);
        background-color: var(--sensor-danger-bg);
      }
    }
  }

  /* 값 색상 (테이블/카드 공용) */
  .v-good {
    color: var(--sensor-good);
  }
  .v-caution {
    color: var(--sensor-caution);
  }
  .v-warning {
    color: var(--sensor-warning);
  }
  .v-danger {
    color: var(--sensor-danger);
  }
  .v-none {
    color: var(--sensor-none);
    font-weight: 400;
  }

  /* 본문(테이블)이 들어가는 가변 영역 */
  .sensor-body {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    /* 정지(수동) 모드에서는 좌우 드래그로 페이지 이동 */
    &.manual {
      cursor: grab;
      touch-action: pan-y;
      user-select: none;
      &:active {
        cursor: grabbing;
      }
    }
  }

  /* 컬럼 헤더는 고정이므로 페이드하지 않고, 본문(값)만 페이드한다(SensorTable tbody 참고) */
  .page-fade {
    flex: 1 1 auto;
    min-height: 0;
  }
`;

/* ── 테이블 뷰 (대안 ① 2단 그룹 헤더) ───────────────────────── */
export const SensorTable = styled.table`
  width: 100%;
  /* 행이 적어도 위젯 높이를 꽉 채우도록 세로로 늘림 (남는 공간은 본문 행에 분배) */
  height: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 0.95rem;
  text-align: center;

  th,
  td {
    padding: 0.5rem 0.375rem;
    border: 1px solid var(--outline);
    vertical-align: middle;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
  /* 제일 바깥(테이블 둘레) 보더만 제거, 내부 구분선은 유지 */
  tr > *:first-child {
    border-left: none;
  }
  tr > *:last-child {
    border-right: none;
  }
  thead tr:first-child > * {
    border-top: none;
  }
  tbody tr:last-child > * {
    border-bottom: none;
  }
  /* 헤더는 내용 높이만, 남는 세로 공간은 본문 행끼리 균등 분배 */
  thead th {
    height: 1px;
  }
  /* 페이지 전환 시 변경되는 데이터(본문)만 부드럽게 페이드. 고정 헤더는 그대로 둔다 */
  tbody {
    animation: sensorBodyFade 1.4s ease-in-out;
  }
  @keyframes sensorBodyFade {
    from {
      opacity: 0.2;
    }
    to {
      opacity: 1;
    }
  }
  tbody tr {
    height: auto;
  }
  th {
    background-color: var(--tonal);
    color: var(--text-secondary);
    font-weight: 600;
    font-size: 1.1em; /* 설치 위치 데이터 컬럼(td.loc-label)과 동일 크기 */
    .sub {
      display: inline; /* 라벨과 같은 줄에 단위 표기 (예: 미세먼지 (µg/m³)) */
      font-size: 0.82em; /* 루트(테이블) 폰트에 비례 → 반응형 */
      /* 단위는 보조 정보 → 센서명(semibold)보다 진하기를 한 톤 낮춰 중요도를 낮춘다 */
      font-weight: var(--font-regular);
      color: var(--text-tertiary);
      opacity: 0.85;
      margin-left: 0.2em;
    }
  }
  /* 다중 센서 그룹의 상단 대분류 헤더 강조(하위 헤더 --tonal 보다 한 단계 진하게) */
  th.group-head {
    color: var(--text-primary);
    background-color: var(--tonal-deep);
  }
  /* 라이트 모드: 헤더를 한 톤 밝은 그레이로 (설치 위치 컬럼도 헤더와 동일 색) */
  html[data-theme='light'] & {
    th,
    td.loc-label {
      background-color: var(--tonal-light);
    }
    th.group-head {
      background-color: var(--tonal);
    }
  }
  /* 다크 모드: 헤더를 어두운 네이비 톤으로, 보더는 기본 outline 보다 어둡게 (설치 위치 컬럼도 헤더와 동일 색) */
  html[data-theme='dark'] & {
    th,
    td {
      border-color: #2a2f3a;
    }
    th,
    td.loc-label {
      background-color: #1a2030;
    }
    th.group-head {
      background-color: #141a28;
    }
  }
  td.loc-label {
    text-align: left;
    font-weight: 600;
    font-size: 1.1em;
    color: var(--text-primary);
    /* 설치 위치 컬럼은 라벨 영역이므로 헤더와 동일한 tonal 배경으로 데이터 컬럼과 구분 */
    background-color: var(--tonal);
    padding-left: 1rem;
    padding-right: 1rem;
    text-overflow: clip;
    .loc-label-fit {
      display: block;
      width: 100%;
      min-width: 0;
      overflow: hidden;
    }
    .loc-label-text {
      display: inline-block;
      max-width: 100%;
      white-space: nowrap;
      font-weight: inherit;
      color: inherit;
      line-height: 1.2;
    }
  }
  /* 측정값(데이터)은 헤더보다 크게(테이블 폰트 대비 em 비례) */
  td .val {
    font-weight: 600;
    font-size: 1.3em;
    vertical-align: middle; /* 칩(.loc-tag)과 동일 기준선으로 수직 중앙 정렬 */
  }
  /* 방식 B 다중 세부위치 : 위치명을 수치값 앞에 '칩' 형태로 표기 (예: [옥상] 60.3)
     큰 수치값 옆에서 묻히지 않도록 tonal 배경(범례 칩과 동일 토큰) + 고대비 텍스트로 시인성 확보 */
  td .loc-tag {
    display: inline-block;
    vertical-align: middle;
    font-size: 0.85em;
    font-weight: 600;
    line-height: 1.4;
    color: var(--text-primary);
    background-color: var(--tonal);
    padding: 0.12em 0.5em;
    border-radius: 0.5rem;
    margin-right: 0.4em;
  }
  /* inline(다중 세부위치) 그룹 셀: 한 칸(colSpan) 안에서 측정점 수만큼 flex 로 완전 균등 분할.
     정수 colSpan 분배와 달리 나눠떨어지지 않아도 각 칸 너비가 정확히 같다. */
  td.inline-merge {
    padding: 0;
  }
  td.inline-merge .inline-cells {
    display: flex;
    width: 100%;
    height: 100%;
  }
  td.inline-merge .inline-cell {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 0.375rem;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  /* 측정점 사이 세로 구분선(원래 컬럼 보더 대체) */
  td.inline-merge .inline-cell + .inline-cell {
    border-left: 1px solid var(--outline);
  }
  html[data-theme='dark'] & td.inline-merge .inline-cell + .inline-cell {
    border-left-color: #2a2f3a;
  }
`;
